/**
 * Link de invitación a un equipo (/invitacion/:token). Público: muestra a qué
 * equipo invitan; con sesión, se acepta y se entra al equipo.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useApp, useToast, errorText } from '../components/AppContext'
import { Brand, ErrorBox, Skeleton } from '../components/ui'
import { EcoApiError } from '../lib/api'
import { useAsync, useDocumentTitle } from '../lib/hooks'

export function AcceptInvite() {
  useDocumentTitle('Invitación')
  const { token = '' } = useParams()
  const { api, session, ready, me, switchOrg } = useApp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const inv = useAsync(() => api.previewInvitation(token), [api, token])

  async function accept() {
    setBusy(true)
    try {
      const { orgId } = await api.acceptInvitation(token)
      // Recarga la página ya dentro del equipo
      switchOrg(orgId)
    } catch (e) {
      toast(errorText(e), 'error')
      setBusy(false)
    }
  }

  const next = encodeURIComponent(`/invitacion/${token}`)
  const i = inv.data
  return (
    <div className="public">
      <div className="public-head">
        <Brand to={session ? '/' : '/login'} />
      </div>
      <div className="page narrow">
        {inv.error ? (
          inv.error instanceof EcoApiError && inv.error.status === 404 ? (
            <div className="empty">
              <h2>Esta invitación no existe</h2>
              <p className="muted">
                Revisa que el link esté completo o pide que te inviten de nuevo.
              </p>
            </div>
          ) : (
            <ErrorBox error={inv.error} onRetry={() => void inv.reload()} />
          )
        ) : !i || !ready ? (
          <div className="stack">
            <Skeleton h={34} w="60%" />
            <Skeleton h={80} />
          </div>
        ) : (
          <div className="card stack" style={{ maxWidth: 520 }}>
            <h1 style={{ margin: 0 }}>Te invitaron a «{i.equipo}»</h1>
            <p className="muted" style={{ margin: 0 }}>
              {i.invitador} te invitó a su equipo en eco. La invitación es para {i.email}.
            </p>
            {i.estado === 'usada' ? (
              <p className="notice warn" style={{ margin: 0 }}>
                Esta invitación ya se usó. {session && <Link to="/">Ir a eco</Link>}
              </p>
            ) : i.estado === 'vencida' ? (
              <p className="notice warn" style={{ margin: 0 }}>
                La invitación venció. Pide que te inviten de nuevo.
              </p>
            ) : session ? (
              <div className="row wrap">
                <button className="btn primary" disabled={busy} onClick={() => void accept()}>
                  {busy ? 'Uniéndote…' : 'Unirme al equipo'}
                </button>
                <span className="faint" style={{ fontSize: 12 }}>
                  Entraste como {me?.user.email ?? '…'}
                </span>
              </div>
            ) : (
              <Link className="btn primary" to={`/login?next=${next}`}>
                Entrar para aceptar
              </Link>
            )}
            <p className="faint" style={{ fontSize: 12, margin: 0 }}>
              Tus reuniones personales no se comparten con el equipo. En el equipo, cada reunión es
              privada de quien la graba hasta que decide compartirla.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
